import { Link, type LinkProps } from '@tanstack/react-router'

import { buttonClass } from '@/components/button-class'
import { cx } from '@/lib/cx'
import { useT } from '@/lib/i18n/translate'
import { pageRange } from '@/lib/paging'

/** Previous / range / next for a paged list; hidden while the list is empty. */
export function Pager({
  label,
  page,
  total,
  link,
}: {
  label: string
  page: number
  total: number
  /** Where page `n` lives — the route and search the list reads. */
  link: (page: number) => LinkProps
}) {
  const t = useT()
  if (total === 0) return null
  const { lastPage, from, to } = pageRange(page, total)
  return (
    <nav aria-label={label} className="flex items-center justify-between gap-2">
      <PagerLink disabled={page <= 1} link={link(page - 1)}>
        {t('common.previous')}
      </PagerLink>
      <span className="text-muted text-sm">{t('common.pageRange', { from, to, total })}</span>
      <PagerLink disabled={page >= lastPage} link={link(page + 1)}>
        {t('common.next')}
      </PagerLink>
    </nav>
  )
}

function PagerLink({ link, disabled, children }: { link: LinkProps; disabled: boolean; children: string }) {
  if (disabled) {
    return (
      <span aria-disabled className={cx(buttonClass('secondary'), 'pointer-events-none opacity-50')}>
        {children}
      </span>
    )
  }
  return (
    <Link {...link} className={buttonClass('secondary')}>
      {children}
    </Link>
  )
}
